import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { PresenceResponse, PresenceState } from '@talkvibe/shared';

import { api } from '../services/api.ts';

/**
 * Who is online, as far as this client knows.
 *
 * Two sources, deliberately: the REST endpoint answers for a list that has
 * just rendered, and the user channel keeps it current afterwards. Neither
 * alone is enough — a socket cannot tell you about people who were already
 * online before you connected, and polling cannot tell you the moment someone
 * leaves.
 *
 * Everyone the server declines to answer for reads as offline, which is the
 * same thing a stranger and an account that has never connected read as. The
 * store does not try to distinguish them, because the server deliberately
 * does not either.
 */
export const usePresenceStore = defineStore('presence', () => {
  const states = ref<Record<string, PresenceState>>({});

  /** Ids already asked about, so a re-render does not re-fetch the same list. */
  const known = new Set<string>();

  function isOnline(userId: string | null | undefined): boolean {
    return userId ? (states.value[userId]?.online ?? false) : false;
  }

  function lastSeenAt(userId: string): string | null {
    return states.value[userId]?.lastSeenAt ?? null;
  }

  const onlineCount = computed(
    () => Object.values(states.value).filter((state) => state.online).length,
  );

  /** Applies an authoritative update from either source. */
  function apply(incoming: PresenceState[]): void {
    if (incoming.length === 0) return;
    const next = { ...states.value };
    for (const state of incoming) {
      next[state.userId] = state;
      known.add(state.userId);
    }
    states.value = next;
  }

  /**
   * Fetches presence for people we have not asked about yet.
   *
   * Callers pass whatever they are rendering; the filtering happens here so a
   * component does not have to track what has already been requested.
   */
  async function prime(userIds: string[]): Promise<void> {
    const missing = [...new Set(userIds)].filter((id) => id.length > 0 && !known.has(id));
    if (missing.length === 0) return;

    // Mark before the request, not after: two lists rendering in the same tick
    // would otherwise both ask for the same people.
    for (const id of missing) known.add(id);

    try {
      const response = await api.get<PresenceResponse>(
        `/api/presence?userIds=${encodeURIComponent(missing.join(','))}`,
      );
      apply(response.states);
    } catch {
      // Presence is decoration. Allow a later render to try again.
      for (const id of missing) known.delete(id);
    }
  }

  function reset(): void {
    states.value = {};
    known.clear();
  }

  return { states, isOnline, lastSeenAt, onlineCount, apply, prime, reset };
});
